import React from 'react';
import { motion } from 'motion/react';
import { Users, Trophy, Star, Target } from 'lucide-react';
import { Player, Match, Team, Config } from '../../types';
import { getCategoryStyles } from '../../lib/categoryStyles';
import { FadeInImage } from '../common/FadeInImage';

interface PlayerCardPremiumProps {
  player: Player;
  playerMatches: Match[];
  matchesCount?: number;
  wins: number;
  losses: number;
  winRate: number;
  categoryRank: number;
  config: Config;
  isEnRacha: boolean;
  onStreakClick?: () => void;
  innerRef?: React.RefObject<HTMLDivElement>;
  layoutId?: string;
  hideShadow?: boolean;
  circuitName?: string;
  circuitLogo?: string;
  isUnranked?: boolean;
}

export function PlayerCardPremium({
  player,
  playerMatches,
  matchesCount,
  wins,
  losses,
  winRate,
  categoryRank,
  config,
  isEnRacha,
  onStreakClick,
  innerRef,
  layoutId,
  hideShadow,
  circuitName,
  circuitLogo,
  isUnranked
}: PlayerCardPremiumProps) {
  const catStyles = getCategoryStyles(player.category);

  const getStarsCount = (category: string) => {
    const cat = (category || '').toLowerCase();
    if (cat.includes('1ra') || cat.includes('primera')) return 8;
    if (cat.includes('2da') || cat.includes('segunda')) return 7;
    if (cat.includes('3ra') || cat.includes('tercera')) return 6;
    if (cat.includes('4ta') || cat.includes('cuarta')) return 5;
    if (cat.includes('5ta') || cat.includes('quinta')) return 4;
    if (cat.includes('6ta') || cat.includes('sexta')) return 3;
    if (cat.includes('7ma') || cat.includes('septima') || cat.includes('séptima')) return 2;
    if (cat.includes('8va') || cat.includes('octava')) return 1;
    return 0;
  };

  const trophies = {
    oro: player.achievements?.filter(a => a.type === 'oro').length || 0,
    plata: player.achievements?.filter(a => a.type === 'plata').length || 0,
    bronce: player.achievements?.filter(a => a.type === 'bronce').length || 0,
  };

  return (
    <motion.div
      ref={innerRef as any}
      layoutId={layoutId}
      className={`relative w-[340px] max-w-full aspect-[3/4] rounded-[24px] overflow-hidden ${hideShadow ? '' : 'shadow-[0_15px_15px_rgba(0,0,0,0.5)]'} bg-[#1e293b] block shrink-0 mx-auto transform-gpu`}
      style={{ willChange: 'transform' }}
    >
      {/* === BACKGROUND LAYER === */}
      <div className="absolute inset-0 bg-[#1e293b]" />

      {/* === DYNAMIC METALLIC FRAME === */}
      <div className="absolute inset-0 pointer-events-none z-[60] rounded-[24px]">
        {/* Metallic Base Layer: Now with 3D Bevel/Bisel effect */}
        <div
          className="absolute inset-0 rounded-[24px] shadow-[inset_0_2px_3px_rgba(255,255,255,0.5),inset_0_-2px_3px_rgba(0,0,0,0.7)]"
          style={{
            background: `linear-gradient(225deg, #000 0%, ${catStyles.hex}44 20%, ${catStyles.hex} 50%, ${catStyles.hex}44 80%, #000 100%)`,
            padding: '10px',
            WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
            WebkitMaskComposite: 'destination-out',
            maskComposite: 'exclude',
          }}
        />

        {/* Inner Definition Edge (Refined for 3D depth) */}
        <div className="absolute inset-[10px] rounded-[14px] pointer-events-none border-t border-white/30 border-b border-black/50 border-x border-white/5" />
      </div>





      {/* === PHOTO SECTION (TOP 70%) === */}
      <div className="absolute inset-0 overflow-hidden rounded-[24px]">
        {player.photoUrl ? (
          <FadeInImage
            src={player.photoUrl}
            alt={player.name}
            containerClassName="w-full h-[75%]"
            className="w-full h-full object-cover object-top contrast-[1.1] brightness-[0.9]"
            crossOrigin="anonymous"
          />
        ) : (
          <div className="w-full h-[75%] flex items-center justify-center bg-neutral-900">
            <Users size={80} className="opacity-10 text-white" />
          </div>
        )}

        {/* Photo Overlays: Soft Vignette (Base Layer above photo) */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.4) 80%, rgba(0,0,0,1) 100%)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1e293b] via-transparent to-transparent z-[1]" />

        {/* Holographic Background Projection (Micro Tech Seal - Offset) */}
        {config.logoUrl && (
          <div className="absolute top-[25%] left-[6%] w-[60px] h-[60px] opacity-[0.55] pointer-events-none mix-blend-screen select-none">
            <FadeInImage
              src={config.logoUrl}
              className="w-full h-full object-contain filter invert brightness-150 contrast-125 animate-[pulse_4s_infinite]"
              style={{
                filter: 'invert(1) brightness(1.5) drop-shadow(6px 0px 4px rgba(0,255,255,0.7)) drop-shadow(-6px 0px 4px rgba(255,0,255,0.7))'
              }}
              alt="Holographic Projection"
              crossOrigin="anonymous"
              noPlaceholderBg={true}
            />
          </div>
        )}
        {/* === MINIMALIST ASCENSION OVERLAY (Parent-Masked for Precision) === */}
        <div className="absolute inset-0 pointer-events-none z-[5] rounded-[24px]"
          style={{
            WebkitMaskImage: `
              linear-gradient(to right, black 0%, transparent 30%, transparent 70%, black 100%),
              linear-gradient(to bottom, transparent 0%, transparent 10%, black 30%, black 60%, transparent 80%)
            `,
            maskImage: `
              linear-gradient(to right, black 0%, transparent 30%, transparent 70%, black 100%),
              linear-gradient(to bottom, transparent 0%, transparent 10%, black 30%, black 60%, transparent 80%)
            `,
            WebkitMaskComposite: 'source-in',
            maskComposite: 'intersect'
          }}
        >
          {/* Top Layer: Minimalist Upward Chevrons - Staggered */}
          <div className="absolute inset-0"
            style={{
              background: `linear-gradient(to bottom, ${catStyles.hex} 0%, #000000 100%)`,
              WebkitMaskImage: `
                url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3E%3Cpath d='M4 14 L10 8 L16 14' fill='none' stroke='black' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E"),
                url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3E%3Cpath d='M4 14 L10 8 L16 14' fill='none' stroke='black' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")
              `,
              maskImage: `
                url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3E%3Cpath d='M4 14 L10 8 L16 14' fill='none' stroke='black' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E"),
                url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3E%3Cpath d='M4 14 L10 8 L16 14' fill='none' stroke='black' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")
              `,
              WebkitMaskSize: '16px 16px',
              maskSize: '16px 16px',
              WebkitMaskPosition: '0 0, 8px 8px',
              maskPosition: '0 0, 8px 8px',
              WebkitMaskRepeat: 'round',
              maskRepeat: 'round',
              WebkitMaskComposite: 'source-over',
              maskComposite: 'add',
              mixBlendMode: 'multiply',
              opacity: 0.8,
              filter: `drop-shadow(0 0 1px ${catStyles.hex}44)`
            }}
          />
          {/* Subtle Vertical Flow Accents */}
          <div className="absolute inset-0"
            style={{
              background: `linear-gradient(to bottom, ${catStyles.hex} 0%, #000 100%)`,
              WebkitMaskImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 40 40'%3E%3Cpath d='M20 0 L20 40' stroke='black' stroke-width='0.5' opacity='0.3'/%3E%3C/svg%3E")`,
              maskImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 40 40'%3E%3Cpath d='M20 0 L20 40' stroke='black' stroke-width='0.5' opacity='0.3'/%3E%3C/svg%3E")`,
              WebkitMaskSize: '40px 40px',
              maskSize: '40px 40px',
              opacity: 0.4,
              mixBlendMode: 'multiply'
            }}
          />
        </div>
      </div>

      {/* === COMPOSITE TECHNICAL PERIMETER === */}
      <div
        className="absolute inset-[10px] pointer-events-none z-30"
        style={{
          WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 20%, black 100%)',
          maskImage: 'linear-gradient(to bottom, transparent 0%, black 20%, black 100%)'
        }}
      >
        <div className="relative w-full h-full border border-white/10 rounded-t-[1.8rem] rounded-b-[0.8rem] overflow-hidden">
          {/* Segmented HUD Lines */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/3 h-[1px] bg-gradient-to-r from-transparent via-white/40 to-transparent" />
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1/3 h-[1px] bg-gradient-to-r from-transparent via-white/40 to-transparent" />
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[1px] h-1/3 bg-gradient-to-b from-transparent via-white/40 to-transparent" />
          <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[1px] h-1/3 bg-gradient-to-b from-transparent via-white/40 to-transparent" />

          {/* Corner Bracket Accents (Aligned with Cat Color) */}
          <div
            className="absolute top-0 left-0 w-10 h-10 border-t-2 border-l-2 rounded-tl-[1.8rem] opacity-50"
            style={{
              borderColor: catStyles.hex,
              WebkitMaskImage: 'radial-gradient(circle at top left, black 20%, transparent 80%)',
              maskImage: 'radial-gradient(circle at top left, black 20%, transparent 80%)'
            }}
          />
          <div
            className="absolute top-0 right-0 w-10 h-10 border-t-2 border-r-2 rounded-tr-[1.8rem] opacity-50"
            style={{
              borderColor: catStyles.hex,
              WebkitMaskImage: 'radial-gradient(circle at top right, black 20%, transparent 80%)',
              maskImage: 'radial-gradient(circle at top right, black 20%, transparent 80%)'
            }}
          />
          <div
            className="absolute bottom-[4px] left-[4px] w-8 h-8 border-b-2 border-l-2 rounded-bl-[10px] opacity-60"
            style={{
              borderColor: catStyles.hex,
              WebkitMaskImage: 'radial-gradient(circle at bottom left, black 20%, transparent 80%)',
              maskImage: 'radial-gradient(circle at bottom left, black 20%, transparent 80%)'
            }}
          />
          <div
            className="absolute bottom-[4px] right-[4px] w-8 h-8 border-b-2 border-r-2 rounded-br-[10px] opacity-60"
            style={{
              borderColor: catStyles.hex,
              WebkitMaskImage: 'radial-gradient(circle at bottom right, black 20%, transparent 80%)',
              maskImage: 'radial-gradient(circle at bottom right, black 20%, transparent 80%)'
            }}
          />

          {/* Diagonal Technical Pattern (Fading Up) */}
          <div
            className="absolute inset-0 pointer-events-none opacity-[0.08]"
            style={{
              backgroundImage: 'repeating-linear-gradient(135deg, #fff 0, #fff 1px, transparent 0, transparent 6px)',
              WebkitMaskImage: 'linear-gradient(to top, black 0%, transparent 70%)',
              maskImage: 'linear-gradient(to top, black 0%, transparent 70%)'
            }}
          />
        </div>
      </div>

      {/* === COMPACT PREMIUM HEADER === */}
      <div className="absolute top-0 left-0 right-0 z-[100] pointer-events-none">
        {/* RANKING & POINTS (LEFT) - Reverted to Clean Flat Style */}
        <div className="absolute top-4 left-[-20px] flex flex-col gap-1.5 transform -skew-x-12">
          {/* Main Rank Box */}
          <div
            className="relative z-10 bg-black/95 border-b-4 border-r border-t border-white/20 border-white/10 backdrop-blur-2xl pl-10 pr-6 py-1.5 flex flex-col items-center justify-center shadow-[0_15px_35px_rgba(0,0,0,0.9),inset_0_1px_0_rgba(255,255,255,0.15)] rounded-r-xl min-h-[40px]"
            style={{ borderBottomColor: catStyles.hex }}
          >
            <div className="transform skew-x-12 flex flex-col items-center justify-center">
              {isUnranked ? (
                <div className="flex items-center gap-1.5">
                  <Trophy size={14} className="text-white/30 shrink-0" />
                  <span className="text-[10px] font-black uppercase tracking-widest text-white/50 leading-none mt-[1px]" style={{ fontFamily: 'Oswald, sans-serif' }}>
                    S/CLASIF.
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-1.5">
                  {!circuitName ? (
                    <img src={config.logoUrl || '/pwa-192x192.png'} alt="Global" className="w-9 h-9 md:w-10 md:h-10 rounded-full object-contain border border-white/20 shrink-0 shadow-lg -ml-3 mr-1 bg-black/40" />
                  ) : circuitLogo ? (
                    <img src={circuitLogo} alt="Club" className="w-9 h-9 md:w-10 md:h-10 rounded-full object-cover border border-white/20 shrink-0 shadow-lg -ml-3 mr-1" />
                  ) : (
                    <Trophy size={18} className="text-white/40 shrink-0" />
                  )}
                  <span
                    className="text-[32px] font-black italic tracking-tighter text-white leading-none"
                    style={{
                      fontFamily: 'Bebas Neue, sans-serif',
                      textShadow: `0 0 15px ${catStyles.hex}40`
                    }}
                  >
                    #{categoryRank > 0 ? categoryRank : '-'}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Stars below points */}
          <div className="transform skew-x-12 ml-12 mt-1.5 flex flex-col gap-3">
            {Array.from({ length: getStarsCount(player.category) }).map((_, i) => (
              <Star 
                key={i} 
                size={7} 
                fill={catStyles.hex} 
                color={catStyles.hex} 
                className="shrink-0 transition-all duration-300 drop-shadow-[0_0_3px_rgba(0,0,0,0.8)]"
              />
            ))}
          </div>
        </div>

        {/* CATEGORY & GENDER (RIGHT) - Metallic Forged Plate */}
        <div className="absolute top-4 right-[-20px] flex flex-col items-end gap-1.5 transform -skew-x-12">
          {/* Category Plate */}
          <div
            className="relative z-10 border-b-4 border-l border-t border-white/20 backdrop-blur-2xl pr-12 pl-10 py-1.5 flex items-center justify-center shadow-[0_20px_40px_rgba(0,0,0,0.8),inset_0_1px_1px_rgba(255,255,255,0.3)] rounded-l-xl relative overflow-hidden"
            style={{
              borderBottomColor: catStyles.hex,
              background: `linear-gradient(135deg, #111 0%, ${catStyles.hex}44 40%, ${catStyles.hex}88 50%, ${catStyles.hex}44 60%, #1a1a1a 100%)`
            }}
          >
            {/* Fast High-Intensity Metallic Sweep (Left to Right) */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent animate-[shimmer_4s_infinite_linear]" />
            </div>

            <div className="transform skew-x-12 relative z-10">
              <span
                className="text-[32px] font-black italic tracking-tighter text-white leading-none drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]"
                style={{
                  fontFamily: 'Bebas Neue, sans-serif',
                  textShadow: `0 0 15px ${catStyles.hex}60`
                }}
              >
                {player.category || '?'}
              </span>
            </div>
          </div>

          {/* Gender Tag */}
          <div
            className="relative z-20 pr-10 pl-6 py-1 bg-white text-black font-black uppercase tracking-[0.2em] text-[8px] shadow-xl rounded-l-sm"
            style={{ fontFamily: 'Oswald, sans-serif' }}
          >
            <div className="transform skew-x-12">
              {player.gender === 'Masculino' ? 'MASCULINO' : player.gender === 'Femenino' ? 'FEMENINO' : 'OPEN'}
            </div>
          </div>
        </div>
      </div>





      {/* === FLOATING STATS COLUMN (RIGHT CENTERED) === */}
      <div className="absolute right-6 top-[48%] -translate-y-1/2 z-50 flex flex-col gap-3">
        {[
          { label: 'PJ', val: matchesCount ?? playerMatches.length },
          { label: 'WIN', val: wins },
          { label: 'WIN %', val: `${winRate.toFixed(0)}` },
        ].map((stat, i) => (
          <div
            key={i}
            className="w-[48px] h-[46px] flex flex-col backdrop-blur-md shadow-2xl transform -skew-x-12 overflow-hidden rounded-md"
            style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}
          >
            {/* Top Section: Muted Category Color */}
            <div
              className="h-[16px] flex items-center justify-center w-full"
              style={{ backgroundColor: `${catStyles.hex}80` }}
            >
              <span className="text-[7px] font-black uppercase tracking-[0.1em] text-white/90 transform skew-x-12" style={{ fontFamily: 'Oswald, sans-serif' }}>
                {stat.label}
              </span>
            </div>

            {/* Bottom Section: Value */}
            <div className="flex-1 flex items-center justify-center w-full">
              <span className="text-[18px] font-black italic text-white/90 transform skew-x-12 leading-none" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                {stat.val}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* === BOTTOM SECTION CONTENT === */}
      <div className="absolute bottom-0 left-0 right-0 z-10 px-6 pb-5 pt-12" style={{
        background: 'linear-gradient(to top, rgba(0,0,0,0.9) 60%, transparent)'
      }}>
        {/* On Fire Tag (Just above nickname) */}
        {isEnRacha && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-1.5 bg-red-600 px-2.5 py-0.5 rounded-md shadow-[0_0_15px_rgba(220,38,38,0.3)] mb-2"
          >
            <span className="text-white text-[7px] font-black uppercase tracking-widest italic" style={{ fontFamily: 'Oswald, sans-serif' }}>EN RACHA</span>
            <span className="text-[10px] leading-none">🔥</span>
          </motion.div>
        )}

        {/* Nickname & Position row */}
        <div className="flex items-center gap-3 mb-1">
          {player.nickname && (
            <span className="text-[11px] font-black uppercase tracking-[0.3em] italic" style={{ fontFamily: 'Oswald, sans-serif', color: catStyles.hex }}>
              "{player.nickname}"
            </span>
          )}
          {player.position && (
            <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-white/5 border border-white/10">
              <span className="text-[8px] font-black uppercase tracking-widest text-white/50" style={{ fontFamily: 'Oswald, sans-serif' }}>
                Pos: {player.position}
              </span>
            </div>
          )}
        </div>

        {/* Player Name */}
        <h2
          className="text-[34px] leading-[0.9] font-black uppercase italic tracking-tighter text-white mb-2"
          style={{
            fontFamily: 'Oswald, sans-serif',
            textShadow: '0 0 20px rgba(0,0,0,0.5)'
          }}
        >
          {player.name}
        </h2>

        {/* Info Row Divider: Metallic Category Line */}
        <div
          className="h-[2px] w-full mb-1"
          style={{
            background: `linear-gradient(90deg, transparent, ${catStyles.hex}44 10%, ${catStyles.hex} 50%, ${catStyles.hex}44 90%, transparent)`
          }}
        />

        {/* Info Row: Stars & Achievements */}
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-1">
            {/* Trophies */}
            <div className="flex items-center gap-2 transform translate-x-1.5">
              <div className={`flex items-center gap-1 transition-opacity ${trophies.oro === 0 ? 'opacity-40 grayscale' : ''}`}>
                <Trophy size={15} className="text-yellow-400" />
                <span className="text-[19px] font-bold text-yellow-400 leading-none" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{trophies.oro}</span>
              </div>
              <div className={`flex items-center gap-1 transition-opacity ${trophies.plata === 0 ? 'opacity-40 grayscale' : ''}`}>
                <Trophy size={13} className="text-slate-300" />
                <span className="text-[16px] font-bold text-slate-300 leading-none" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{trophies.plata}</span>
              </div>
              <div className={`flex items-center gap-1 transition-opacity ${trophies.bronce === 0 ? 'opacity-40 grayscale' : ''}`}>
                <Trophy size={13} className="text-orange-400" />
                <span className="text-[16px] font-bold text-orange-400 leading-none" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{trophies.bronce}</span>
              </div>
            </div>
          </div>

          {/* Branding / Official Badge */}
          <div className="flex items-center gap-1.5 opacity-80">
            <div className="flex flex-col items-end leading-[1.1]">
              <span className="text-[9px] font-black uppercase tracking-[0.1em] text-white mb-0.5" style={{ fontFamily: 'Oswald, sans-serif' }}>{config.pageName ? config.pageName.toUpperCase() : 'ROCA PADEL TOUR'}</span>
              
              {/* Simple Validation Badge */}
              <div 
                className={`flex items-center gap-1.5 px-2 py-0.5 rounded border transform translate-x-1 ${
                  player.isValidated 
                    ? 'bg-white/5 border-white/20 shadow-lg' 
                    : 'bg-black/40 border-white/10 opacity-70'
                }`}
                style={{ 
                  borderColor: player.isValidated ? `${catStyles.hex}44` : 'rgba(255,255,255,0.15)',
                }}
              >
                {!player.isValidated && (
                  <div className="w-1 h-1 rounded-full bg-red-500 animate-pulse" />
                )}
                <span 
                  className="text-[7px] font-black uppercase tracking-[0.2em]" 
                  style={{ 
                    fontFamily: 'Oswald, sans-serif',
                    color: player.isValidated ? catStyles.hex : 'rgba(255,255,255,0.7)'
                  }}
                >
                  {player.isValidated ? 'JUGADOR OFICIAL' : 'VALIDACIÓN PENDIENTE'}
                </span>
              </div>
            </div>
            {config.logoUrl ? (
              <FadeInImage src={config.logoUrl} className="h-12 object-contain" alt="RPT" crossOrigin="anonymous" noPlaceholderBg={true} />
            ) : (
              <Trophy size={24} className="text-white/20" />
            )}
          </div>
        </div>
      </div>

      {/* Gloss Effect */}
      <div className="absolute inset-0 pointer-events-none opacity-20 bg-gradient-to-tr from-transparent via-white/5 to-transparent" />

      {/* Subtle Vignette */}
      <div className="absolute inset-0 pointer-events-none shadow-[inset_0_0_100px_rgba(0,0,0,0.6)]" />
    </motion.div>
  );
}
